import { useState, useMemo } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { Timestamp } from '@britishcouncil/common-core';
import { getDisplayValue, getYearPages, MONTHS_SHORT, parseValue } from './yearMonthPickerHelpers';
import { YearMonthPickerProps } from './yearMonthPickerTypes';
import { YearMonthPickerNavigation } from './YearMonthPickerNavigation';
import { S } from './yearMonthPickerStyles';

const MAX_YEAR = Timestamp.getYear(Timestamp.now());

export const YearMonthPicker = ({
  value,
  onChange,
  mode = 'year',
  minYear = 2000,
  maxYear = MAX_YEAR,
  placeholder,
  disabled,
  error,
  ...dropdownProps
}: YearMonthPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [navigationYear, setNavigationYear] = useState(maxYear);

  const parsedValue = useMemo(() => parseValue(value), [value]);
  const yearPages = useMemo(() => getYearPages(minYear, maxYear), [minYear, maxYear]);
  const [currentYearPage, setCurrentYearPage] = useState(yearPages.length - 1);

  const handleYearSelect = (year: number) => {
    const dateValue = Timestamp.create(year, 0, 1, 0, 0, 0, 0);
    onChange(Timestamp.toISOString(dateValue));
    setIsOpen(false);
  };

  const handleMonthSelect = (month: number) => {
    const dateValue = Timestamp.create(navigationYear, month, 1, 0, 0, 0, 0);
    onChange(Timestamp.toISOString(dateValue));
    setIsOpen(false);
  };

  const handleOpen = () => {
    if (disabled) return;

    const year = parsedValue.year ?? maxYear;
    const yearPage = yearPages.findIndex((page) => page.includes(year)) ?? yearPages.length - 1;

    setCurrentYearPage(yearPage);
    setNavigationYear(year);
    setIsOpen(true);
  };

  return (
    <S.DropdownWrapper>
      <S.Dropdown
        trigger={
          <S.Input
            value={getDisplayValue(parsedValue, mode)}
            icon="calendar alternate outline"
            iconPosition="left"
            error={error}
            placeholder={placeholder || (mode === 'year' ? 'Select year' : 'Select month and year')}
          />
        }
        icon={null}
        onClose={() => setIsOpen(false)}
        onOpen={handleOpen}
        open={isOpen}
        disabled={disabled}
        error={error}
        {...dropdownProps}
      >
        <Dropdown.Menu>
          <YearMonthPickerNavigation
            currentYearPage={currentYearPage}
            navigationYear={navigationYear}
            yearPages={yearPages}
            mode={mode}
            minYear={minYear}
            maxYear={maxYear}
            setCurrentYearPage={setCurrentYearPage}
            setNavigationYear={setNavigationYear}
          />

          {mode === 'year' ? (
            <S.GridMenu attached="bottom">
              {yearPages[currentYearPage]?.map((year) => (
                <S.Button
                  key={year}
                  basic
                  active={parsedValue.year === year}
                  primary={parsedValue.year === year}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleYearSelect(year);
                  }}
                  type="button"
                  content={year}
                />
              ))}
            </S.GridMenu>
          ) : (
            <S.GridMenu attached="bottom">
              {MONTHS_SHORT.map((month, index) => (
                <S.Button
                  key={month}
                  basic
                  active={parsedValue.year === navigationYear && parsedValue.month === index}
                  primary={parsedValue.year === navigationYear && parsedValue.month === index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMonthSelect(index);
                  }}
                  content={month}
                />
              ))}
            </S.GridMenu>
          )}
        </Dropdown.Menu>
      </S.Dropdown>
    </S.DropdownWrapper>
  );
};
